/**
 * DeepLinkService
 *
 * Generates deep link URLs for menu filtering based on admin configuration.
 * Supports category, brand, and product filters.
 */

export interface DeepLinkConfig {
  storeId: string; // "maryland/olney"
  menuType: 'medical' | 'recreational';
  linkType: 'filter' | 'product' | 'multi-product' | 'special' | null;
  categories?: string[];
  subcategories?: string[];
  brands?: string[];
  weights?: string[];
  productId?: string;
  productIds?: string[]; // For multi-product links - banner deep-links to a curated set
  specialId?: string; // Dutchie deal/special ID for filtering by deal
}

export type Environment = 'qa' | 'production';

const ENVIRONMENT_BASE_URLS: Record<Environment, string> = {
  qa: 'https://sweetspot-qa.web.app',
  production: 'https://sweetspotfarms.com'
};

/**
 * Generate a full deep link URL based on configuration
 */
export function generateDeepLinkUrl(
  config: DeepLinkConfig,
  environment: Environment
): string {
  const baseUrl = ENVIRONMENT_BASE_URLS[environment];
  const { storeId, menuType, linkType } = config;

  // Build base path: /store/{state}/{storeName}/dispensary-{medical|recreational}-menu
  const menuTypeSlug = menuType === 'medical' ? 'medical' : 'recreational';
  const basePath = `/store/${storeId}/dispensary-${menuTypeSlug}-menu`;

  // Build query parameters based on link type
  const params = buildQueryParams(config);

  // Combine into full URL
  const queryString = params ? `?${params}` : '';
  return `${baseUrl}${basePath}${queryString}`;
}

/**
 * Build query parameter string from deep link config
 */
export function buildQueryParams(config: DeepLinkConfig): string {
  const params = new URLSearchParams();

  if (!config.linkType) {
    return '';
  }

  switch (config.linkType) {
    case 'filter':
      // Add categories if present
      if (config.categories && config.categories.length > 0) {
        params.set('categories', config.categories.join(','));
      }
      // Add subcategories if present
      if (config.subcategories && config.subcategories.length > 0) {
        params.set('subcategories', config.subcategories.join(','));
      }
      // Add brands if present
      if (config.brands && config.brands.length > 0) {
        params.set('brands', config.brands.join(','));
      }
      // Add weights if present
      if (config.weights && config.weights.length > 0) {
        params.set('weights', config.weights.join(','));
      }
      break;

    case 'product':
      if (config.productId) {
        params.set('productId', config.productId);
      }
      break;

    case 'multi-product':
      if (config.productIds && config.productIds.length > 0) {
        params.set('productIds', config.productIds.join(','));
      }
      break;

    case 'special':
      if (config.specialId) {
        params.set('specialId', config.specialId);
      }
      break;
  }

  return params.toString();
}

/**
 * Get environment base URL
 */
export function getEnvironmentBaseUrl(environment: Environment): string {
  return ENVIRONMENT_BASE_URLS[environment];
}

/**
 * Validate that a deep link config has all required fields
 */
export function validateDeepLinkConfig(config: DeepLinkConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!config.storeId) {
    errors.push('Store ID is required');
  }

  if (!config.menuType) {
    errors.push('Menu type is required');
  }

  if (!config.linkType) {
    return { isValid: true, errors: [] }; // No link type means no link (valid)
  }

  // Validate based on link type
  switch (config.linkType) {
    case 'filter':
      // Require at least one filter (categories OR subcategories OR brands OR weights)
      if ((!config.categories || config.categories.length === 0) &&
          (!config.subcategories || config.subcategories.length === 0) &&
          (!config.brands || config.brands.length === 0) &&
          (!config.weights || config.weights.length === 0)) {
        errors.push('At least one filter (category, subcategory, brand, or weight) is required for filter links');
      }
      break;

    case 'product':
      if (!config.productId) {
        errors.push('Product ID is required for product links');
      }
      break;

    case 'multi-product':
      if (!config.productIds || config.productIds.length === 0) {
        errors.push('At least one product is required for multi-product links');
      }
      break;

    case 'special':
      if (!config.specialId) {
        errors.push('Special ID is required for special/deal links');
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Format a deep link config into a human-readable description
 */
export function formatDeepLinkDescription(config: DeepLinkConfig): string {
  if (!config.linkType) {
    return 'No link configured';
  }

  switch (config.linkType) {
    case 'filter':
      const parts: string[] = [];
      if (config.categories && config.categories.length > 0) {
        parts.push(`Categor${config.categories.length > 1 ? 'ies' : 'y'}: ${config.categories.join(', ')}`);
      }
      if (config.subcategories && config.subcategories.length > 0) {
        parts.push(`Subcategor${config.subcategories.length > 1 ? 'ies' : 'y'}: ${config.subcategories.join(', ')}`);
      }
      if (config.brands && config.brands.length > 0) {
        parts.push(`Brand${config.brands.length > 1 ? 's' : ''}: ${config.brands.join(', ')}`);
      }
      if (config.weights && config.weights.length > 0) {
        parts.push(`Weight${config.weights.length > 1 ? 's' : ''}: ${config.weights.join(', ')}`);
      }
      return parts.length > 0 ? parts.join(', ') : 'No filters selected';

    case 'product':
      return `Product: ${config.productId}`;

    case 'multi-product': {
      const count = config.productIds?.length ?? 0;
      return `${count} selected product${count === 1 ? '' : 's'}`;
    }

    case 'special':
      return `Dutchie Deal: ${config.specialId}`;

    default:
      return 'Unknown link type';
  }
}

export default {
  generateDeepLinkUrl,
  buildQueryParams,
  getEnvironmentBaseUrl,
  validateDeepLinkConfig,
  formatDeepLinkDescription
};
